<template>
  <div class="ts-space-y-2">
    <!-- Customer — full width -->
    <div class="row">
      <div class="col-md-12">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.customer') }}</label>
        <a-select v-model="model.customer_id" :options="customerProfiles" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('customer_id') }" placeholder="Select"></a-select>
        <div class="validate" v-if="errors.has('customer_id')">{{ errors.first('customer_id') }}</div>
      </div>
    </div>

    <!-- Serial Card | Promotion Type -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.serialCard') }}</label>
        <input v-model.trim="model.serial_card" type="text" class="form-control"
          :class="{ 'is-invalid': errors.has('serial_card') }" style="height: 32px;" />
        <div class="validate" v-if="errors.has('serial_card')">{{ errors.first('serial_card') }}</div>
      </div>
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.promotionType') }}</label>
        <a-select v-model="model.promotion_type_id" :options="promotionTypeList" class="ts-w-full"
          :class="{ 'is-invalid': errors.has('promotion_type_id') }" placeholder="Select"
          @change="onPromotionTypeChange"></a-select>
        <div class="validate" v-if="errors.has('promotion_type_id')">{{ errors.first('promotion_type_id') }}</div>
      </div>
    </div>

    <!-- Max Times | Status -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.maxTime') }}</label>
        <input v-model.number="model.max_times" type="number" class="form-control" style="height: 32px;" />
      </div>
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.status') }}</label>
        <a-select v-model="model.status_id" :options="statusList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('status_id') }" placeholder="Select"></a-select>
        <div class="validate" v-if="errors.has('status_id')">{{ errors.first('status_id') }}</div>
      </div>
    </div>

    <!-- Activate Date | Expiry Date -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.activateDate') }}</label>
        <a-date-picker v-model="model.activate_date" class="ts-w-full" show-time format="DD-MM-YYYY HH:mm:ss"
          valueFormat="DD-MM-YYYY HH:mm:ss" :class="{ 'is-invalid': errors.has('activate_date') }"></a-date-picker>
        <div class="validate" v-if="errors.has('activate_date')">{{ errors.first('activate_date') }}</div>
      </div>
      <div class="col-md-6">
        <label class="ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.expiryDate') }}</label>
        <a-date-picker v-model="model.expiry_date" class="ts-w-full" show-time format="DD-MM-YYYY HH:mm:ss"
          valueFormat="DD-MM-YYYY HH:mm:ss" :class="{ 'is-invalid': errors.has('expiry_date') }"></a-date-picker>
        <div class="validate" v-if="errors.has('expiry_date')">{{ errors.first('expiry_date') }}</div>
      </div>
    </div>

    <!-- Card Type -->
    <div class="row">
      <div class="col-md-12">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.cardType') }}</label>
        <a-select v-model="model.card_type_id" :options="cardTypeList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('card_type_id') }" placeholder="Select"
          @change="onCardTypeChange"></a-select>
        <div class="validate" v-if="errors.has('card_type_id')">{{ errors.first('card_type_id') }}</div>
      </div>
    </div>

    <!-- Promotion Package — full width + detail table -->
    <div class="row" v-if="model.promotion_type_id === 2">
      <div class="col-md-12">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.promotion') }}</label>
        <a-select v-model="model.package_id" :options="promotionList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('package_id') }" placeholder="Select" allow-clear
          @change="onPackageChange"></a-select>
        <div class="validate" v-if="errors.has('package_id')">{{ errors.first('package_id') }}</div>
      </div>
      <div class="col-md-12 ts-mt-2" v-if="selectedPackage">
        <div class="pkg-record">
          <div class="pkg-header ts-flex ts-items-center ts-justify-between">
            <h4 class="ts-text-sm ts-font-semibold ts-text-gray-700 ts-m-0 ts-flex ts-items-center">
              <span class="ts-inline-flex ts-items-center ts-justify-center ts-rounded ts-mr-2"
                style="width:24px;height:24px;background:#e6f4ff;">
                <i class="fa fa-gift" style="color:#1890ff;font-size:11px;"></i>
              </span>
              {{ selectedPackage.package_name }}
            </h4>
          </div>
          <div class="ts-p-3">
            <div class="table-ui-detail">
              <table>
                <thead>
                  <tr>
                    <th width="3%" class="ts-text-center">{{ $t('no') }}</th>
                    <th width="10%">{{ $t('promotionPackage.barcode') }}</th>
                    <th>{{ $t('promotionPackage.product') }}</th>
                    <th width="8%">{{ $t('promotionPackage.uom') }}</th>
                    <th width="8%" class="ts-text-right">{{ $t('promotionPackage.quantity') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.unitPrice') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.discount') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.total') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, i) in selectedPackage.details" :key="d.package_detail_id">
                    <td class="ts-text-center ts-whitespace-no-wrap">{{ i + 1 }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.barcode }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.product_name_en }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.uom_name_en }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.quantity }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.unit_price }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">
                      <span v-if="d.discount_rate">{{ d.discount_rate }}%</span>
                      <span v-else-if="d.discount_amount">{{ d.discount_amount }}</span>
                      <span v-else>-</span>
                    </td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.grand_total }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="background-color: #e3e8f1;">
                    <td colspan="6" class="ts-whitespace-no-wrap ts-align-top" rowspan="3">
                      <b>{{ $t('promotionPackage.row') }} : {{ selectedPackage.details.length }}</b>
                    </td>
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ $t('promotionPackage.total') }} :</b>
                    </td>
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ selectedPackage.sub_total }}</b>
                    </td>
                  </tr>
                  <tr style="background-color: #e3e8f1;">
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ $t('customerMemberCard.maxTime') }} :</b>
                    </td>
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ model.max_times }}</b>
                    </td>
                  </tr>
                  <tr style="background-color: #e3e8f1;">
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ $t('customerMemberCard.grandTotal') }} :</b>
                    </td>
                    <td class="ts-whitespace-no-wrap ts-text-right">
                      <b>{{ selectedPackage.sub_total * (model.max_times || 0) }}</b>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2 ts-mt-3">
        <ts-button :disabled="loading" @click.prevent="$emit('cancel')">
          {{ $t('cancel') }}
        </ts-button>
        <ts-button v-if="!isUpdate" color="primary" @click.prevent="onCheckExist" :disabled="loading">
          {{ $t('save') }}
        </ts-button>
        <ts-button v-if="isUpdate" color="primary" @click.prevent="onUpdate" :disabled="loading">
          {{ $t('update') }}
        </ts-button>
      </div>
    </div>
    <Spin size="large" fix v-if="loading"></Spin>
    <!-- form-action -->
    <a-modal v-if="exist" v-model="exist" centered :header="null" :zIndex="1050" :footer="null" :closable="false"
      width="400px">
      <div class="ts-space-y-2">
        <div class="row">
          <div class="ts-flex ts-items-center ts-gap-3 ts-px-4 ts-pb-3 ts-border-b ts-border-gray-200 ts-w-full">
            <div class="ts-flex-shrink-0">
              <i class="fas fa-exclamation ts-text-orange-500"></i>
            </div>
            <b class="ts-text-sm ts-font-medium ts-text-gray-900">
              Are you sure ?
            </b>
          </div>

          <div class="ts-px-2 ts-pt-2 ts-pb-1 ts-w-full">
            <span class="ts-text-sm ts-text-gray-600">
              This customer is already activated with other serial card. Do you
              want to overwirte it?
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required ts-mb-0 ts-text-xs">{{
              $t('customerMemberCard.remark')
            }}</label>
            <textarea v-model="model.remark" rows="3" class="form-control"
              :class="{ 'is-invalid': errors.has('remark') }" placeholder="Note something here"></textarea>
            <div class="validate" v-if="errors.has('remark')">
              {{ errors.first('remark') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2 ts-mt-3">
            <ts-button :disabled="loading" @click.prevent="$emit('cancel')">
              {{ $t('cancel') }}
            </ts-button>

            <ts-button v-if="!isUpdate" :disabled="loading" @click.prevent="onConfirm" outline color="primary">
              {{ $t('confirm') }}
            </ts-button>
          </div>
        </div>
        <Spin size="large" fix v-if="loading"></Spin>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'customerMemberCard',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      exist: false,
      model: {
        customer_member_card_id: null,
        customer_id: null,
        serial_card: null,
        promotion_type_id: 1,
        package_id: null,
        activate_date: moment().format('DD-MM-YYYY HH:mm:ss'),
        expiry_date: null,
        status_id: 1,
        remark: null,
        overwirte: false,
        max_times: null,
        card_type_id: null
      }
    }
  },
  created() {
    this.setEditData()
    this.fetchData()
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['edit_data', 'formViewData']),
    isUpdate() {
      return !_.isEmpty(this.edit_data)
    },
    customerProfiles() {
      if (this.formViewData.customer && this.formViewData.customer.length) {
        return this.formViewData.customer.map(el => ({
          label:
            'Code: ' +
            el.customer_code +
            ' | Customer: ' +
            el.customer_name +
            ' | Contact: ' +
            el.contact_number +
            ' | Gender: ' +
            el.gender,
          value: el.customer_id
        }))
      }
      return []
    },
    cardTypeList() {
      if (this.formViewData.cardType && this.formViewData.cardType.length) {
        return this.formViewData.cardType.map(el => ({
          label: el.card_types,
          value: el.card_type_id,
          max_times: el.max_times
        }))
      }
      return []
    },
    promotionList() {
      if (this.formViewData.promotions && this.formViewData.promotions.length) {
        return this.formViewData.promotions.map(el => ({
          label: el.package_name,
          value: el.package_id
        }))
      }
      return []
    },
    selectedPackage() {
      if (!this.model.package_id || !this.formViewData.promotions) return null
      return this.formViewData.promotions.find(p => p.package_id === this.model.package_id) || null
    },
    statusList() {
      return [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 }
      ]
    },
    promotionTypeList() {
      return [
        { label: 'Normal', value: 1 },
        { label: 'Package', value: 2 }
      ]
    }
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    async fetchData() {
      this.loading = true
      await this.getFormViewData({
        params: undefined,
        index: undefined
      }).finally(() => (this.loading = false))
    },
    async onCheckExist() {
      this.loading = true
      await this.$store
      this.$store
        .dispatch('customer/customerMemberCard/checkExist', this.model)
        .then(response => {
          this.exist = response.exist
          if (response.exist) {
            this.loading = false
          } else {
            this.onSave()
          }
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    onConfirm() {
      this.model.overwirte = true
      this.onSave()
    },
    onPackageChange(selectedId) {
      if (!selectedId) this.model.package_id = null
    },
    onPromotionTypeChange(value) {
      if (value === 1) this.model.package_id = null
    },
    onCardTypeChange(selectedId) {
      const selectedCard = this.cardTypeList.find(c => c.value === selectedId)
      this.model.max_times = selectedCard ? selectedCard.max_times : null
    },
    onUpdate() {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/update', {
          id: this.model.customer_member_card_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onSave() {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.customer_member_card_id = this.edit_data.customer_member_card_id
        this.model.customer_id = this.edit_data.customer_id
        this.model.serial_card = this.edit_data.serial_card
        this.model.card_type_id = this.edit_data.card_type_id
        this.model.max_times = this.edit_data.max_times
        this.model.promotion_type_id = this.edit_data.promotion_type_id ? this.edit_data.promotion_type_id : 1
        this.model.package_id = this.edit_data.package_id
        this.model.activate_date = this.edit_data.activate_date
          ? moment(
            this.edit_data.activate_date,
            'DD-MM-YYYY hh:mm:ss A'
          ).format('DD-MM-YYYY HH:mm:ss')
          : null
        this.model.expiry_date = this.edit_data.expiry_date
          ? moment(this.edit_data.expiry_date, 'DD-MM-YYYY hh:mm:ss A').format(
            'DD-MM-YYYY HH:mm:ss'
          )
          : null
        this.model.status_id = this.edit_data.status_id
        this.model.remark = this.edit_data.remark
      }
    },
    clearInput() {
      this.model.customer_member_card_id = null
      this.model.customer_id = null
      this.model.serial_card = null
      this.model.activate_date = moment().format('DD-MM-YYYY HH:mm:ss')
      this.model.expiry_date = null
      this.model.status_id = 1
      this.model.remark = null
      this.model.overwirte = false
      this.model.card_type_id = null
      this.model.promotion_type_id = 1
      this.model.package_id = null
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {})
    }
  }
}
</script>
<style>
.ant-calendar-picker-input {
  height: 32px !important;
}

.pkg-record {
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
}

.pkg-header {
  padding: 10px 16px;
  background: #fafafa;
}
</style>
